import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface OcrField {
  value: string;
  confidence: number;
}

export interface OcrExtractIdCardResponse {
  doc_type?: string;
  fields?: { [key: string]: OcrField };
  needs_review?: boolean;
  raw_text?: string;
  pipeline?: string;
  elapsed_seconds?: number;
  // legacy / fallback
  success?: boolean;
  data?: { [key: string]: any };
  error?: string;
}

export interface OcrPrescriptionResponse {
  success: boolean;
  data?: {
    patient_name?: string;
    doctor_name?: string;
    date?: string;
    problem?: string;
    medications?: Array<{
      name?: string;
      dosage?: string;
      frequency?: string;
      duration?: string;
    }>;
    investigations?: string[];
    advice?: string[];
    follow_up?: string;
    [key: string]: any;
  };
  raw?: any;
  error?: string;
}

export interface OcrLabReportResponse {
  success: boolean;
  data?: {
    patient_name?: string;
    lab_name?: string;
    report_date?: string;
    tests?: Array<{
      name?: string;
      result?: string;
      unit?: string;
      reference_range?: string;
      status?: string;
    }>;
    [key: string]: any;
  };
  raw?: any;
  error?: string;
}

export interface OcrDischargeResponse {
  success: boolean;
  data?: {
    patient_name?: string;
    admission_date?: string;
    discharge_date?: string;
    diagnosis?: string[];
    treatment?: string;
    medications?: Array<{ name?: string; dosage?: string }>;
    follow_up?: string;
    [key: string]: any;
  };
  raw?: any;
  error?: string;
}

export interface OcrPreSurgeryResponse {
  success: boolean;
  data?: { [key: string]: any };
  raw?: any;
  error?: string;
}

export interface OcrInsuranceResponse {
  success: boolean;
  data?: { [key: string]: any };
  raw?: any;
  error?: string;
}

export interface DocumentChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatResponse {
  success: boolean;
  answer?: string;
  session_id?: string;
  suggested_questions?: string[];
  error?: string;
}

export interface StoreDocResponse {
  success: boolean;
  session_id?: string;
  message?: string;
  suggested_questions?: string[];
  error?: string;
}

export interface ClearChatResponse {
  success: boolean;
  message?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OcrService {
  private baseUrl = environment.OCR_BASE_URL;

  constructor(private http: HttpClient) {}

  // ─── OCR: ID Card (Aadhaar / PAN) ───────────────────────────────────────────
  extractIdCard(
    imageBase64: string,
    mediaType: string = 'image/jpeg',
  ): Observable<OcrExtractIdCardResponse> {
    const url = `${this.baseUrl}${environment.OCR_EXTRACT_ID_CARD_API}`;
    return this.http.post<OcrExtractIdCardResponse>(url, {
      image_base64: imageBase64,
      media_type: mediaType,
    });
  }

  // ─── OCR: Prescription ────────────────────────────────────────────────────
  extractPrescription(
    fileBase64: string,
    mediaType: string = 'image/jpeg',
    problem: string = '',
  ): Observable<OcrPrescriptionResponse> {
    const url = `${this.baseUrl}${environment.OCR_PRESCRIPTION_API}`;
    return this.http.post<OcrPrescriptionResponse>(url, {
      file_base64: fileBase64,
      media_type: mediaType,
      problem: problem,
    });
  }

  // ─── OCR: Handwritten Prescription (HTR) ──────────────────────────────────
  recognizeHandwritten(pagesPngB64: string[]): Observable<any> {
    const url = environment.htr_recognize_url;
    const headers = new HttpHeaders({
      'X-API-Key': environment.prescription_ocr_api_key,
    });
    return this.http.post<any>(
      url,
      { pages_png_b64: pagesPngB64 },
      { headers },
    );
  }

  // ─── OCR: Lab Report ─────────────────────────────────────────────────────
  extractLabReport(
    file: File,
    problem: string = '',
  ): Observable<OcrLabReportResponse> {
    const url = `${this.baseUrl}${environment.OCR_LAB_REPORT_API}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('problem', problem);
    return this.http.post<OcrLabReportResponse>(url, formData);
  }

  // ─── OCR: Discharge Summary ───────────────────────────────────────────────
  extractDischargeSummary(
    file: File,
    problem: string = '',
  ): Observable<OcrDischargeResponse> {
    const url = `${this.baseUrl}${environment.OCR_DISCHARGE_SUMMARY_API}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('problem', problem);
    return this.http.post<OcrDischargeResponse>(url, formData);
  }

  // ─── OCR: Pre-Surgery Form ────────────────────────────────────────────────
  extractPreSurgery(
    file: File,
    problem: string = '',
  ): Observable<OcrPreSurgeryResponse> {
    const url = `${this.baseUrl}${environment.OCR_PRE_SURGERY_API}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('problem', problem);
    return this.http.post<OcrPreSurgeryResponse>(url, formData);
  }

  // ─── OCR: Insurance / EOB ────────────────────────────────────────────────
  extractInsurance(
    file: File,
    problem: string = '',
  ): Observable<OcrInsuranceResponse> {
    const url = `${this.baseUrl}${environment.OCR_INSURANCE_API}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('problem', problem);
    return this.http.post<OcrInsuranceResponse>(url, formData);
  }

  // ─── Document Chat: Store Document ───────────────────────────────────────
  storeDocument(file: File, sessionId: string): Observable<StoreDocResponse> {
    const url = `${this.baseUrl}${environment.DOCUMENT_STORE_API}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('session_id', sessionId);
    return this.http.post<StoreDocResponse>(url, formData);
  }

  // ─── Document Chat: Chat ─────────────────────────────────────────────────
  chat(sessionId: string, message: string): Observable<ChatResponse> {
    const url = `${this.baseUrl}${environment.DOCUMENT_CHAT_API}`;
    return this.http.post<ChatResponse>(url, {
      session_id: sessionId,
      message: message,
    });
  }

  // ─── Document Chat: Clear Session ────────────────────────────────────────
  clearChat(sessionId: string): Observable<ClearChatResponse> {
    const url = `${this.baseUrl}${environment.DOCUMENT_CLEAR_SESSION_API}`;
    return this.http.post<ClearChatResponse>(url, {
      session_id: sessionId,
    });
  }

  // ─── Utility: File → Base64 ──────────────────────────────────────────────
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip data URL prefix → keep only the base64 portion
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ─── Utility: Detect media type from file ────────────────────────────────
  getMediaType(file: File): string {
    return file.type || 'image/jpeg';
  }
}
